/** Root totals of a calculate answer that the cart total is derived from, in pence. */
export interface CartPaidTotalsInput {
  base_total: number;
  discount_total: number;
  promocode_discount: number;
  paid_bonuses: number;
  paid_loyalty_points: number;
}

/** A finite positive amount as is, anything else (absent, null, negative) as `0`. */
export const positivePence = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;

/** Amounts the items line of the cart is derived from, in pence. */
export interface PaidWithoutDiscountInput {
  basePence: number;
  /** Every item discount, the member one included. */
  discountPence: number;
  paidBonusesPence: number;
}

/** Items after every item discount and the paid bonuses, in pence. */
export const cartPaidWithoutDiscountPence = ({
  basePence,
  discountPence,
  paidBonusesPence,
}: PaidWithoutDiscountInput): number =>
  basePence - (discountPence + paidBonusesPence);

/** The part of a calculated item its cart prices are read from, in minor units. */
export interface CalculatedItemPricesInput {
  base_price: number;
  salePrice?: number | null;
  discount_price?: number;
  paid_price: number;
  convertedDiscountPrice?: number | null;
  discount_amount: number;
  membership_discount_amount?: number | null;
}

/** Unit prices of a cart item after calculate, in pence. */
export interface CalculatedItemPrices {
  /** Full unit price. */
  price: number;
  /** Unit price after every item discount; `0` without a discount. */
  discountPrice: number;
  /** Unit discount; `0` without a discount. */
  discountAmount: number;
  /** Member part of the unit discount; `0` for converted prices. */
  membershipDiscountAmount: number;
}

/**
 * Unit prices of a calculated item. Converted line-item prices win when the
 * backend sends them (DubaiExpress); the member discount is in base-currency
 * pence, so converted prices never show it.
 */
export const calculatedItemPricesPence = (
  item: CalculatedItemPricesInput,
  convertedPrices: boolean,
): CalculatedItemPrices => {
  const price = item.salePrice ?? item.base_price;
  const paidPrice =
    item.convertedDiscountPrice ??
    (item.discount_price !== undefined ? item.discount_price : item.paid_price);
  return {
    price,
    discountPrice: item.discount_amount ? paidPrice : 0,
    discountAmount: item.discount_amount ? Math.max(0, price - paidPrice) : 0,
    membershipDiscountAmount: convertedPrices
      ? 0
      : positivePence(item.membership_discount_amount),
  };
};

/** The part of a cart item its checkout "Subtotal" row is drawn from, in pence. */
export interface SubtotalItemInput {
  count: number;
  price: number;
  /** Price after every item discount, the member one included; `0` without a discount. */
  discountPrice: number;
  /** Member discount per unit to add back; `0` while the member line is hidden. */
  memberDiscountPence: number;
}

/**
 * Item row under the checkout "Subtotal", in pence: the item before the member
 * discount, so the rows add up to the Subtotal that adds it back. Adding it
 * back never lifts a row above the full price.
 */
export const subtotalItemPence = ({
  count,
  price,
  discountPrice,
  memberDiscountPence: member,
}: SubtotalItemInput): number => {
  const unit = discountPrice
    ? Math.min(
        discountPrice + positivePence(member),
        Math.max(price, discountPrice),
      )
    : price;
  return count * unit;
};

/**
 * Splits the member discount out of the totals for display only, so that it is
 * shown once: the "Discount" line drops it and the checkout "Subtotal" adds it
 * back, keeping Subtotal minus the lines equal to the total. The member line
 * never exceeds the reported discount.
 */
export const cartBreakdownPence = ({
  discountPence,
  paidWithoutDiscountPence,
  memberDiscountPence: member,
}: CartBreakdownInput): CartBreakdownLines => {
  const shownMember = Math.min(Math.max(member, 0), Math.max(discountPence, 0));
  return {
    discountPence: discountPence - shownMember,
    memberDiscountPence: shownMember,
    subtotalPence: paidWithoutDiscountPence + shownMember,
  };
};

/** The part of a calculated order the free-delivery threshold is read from. */
export interface FreeDeliveryThresholdOrder {
  delivery_info?: { threshold?: number | null } | null;
}

/** Amounts the cart and checkout breakdowns are drawn from, in pence. */
export interface CartBreakdownInput {
  /** Root `discount_total`; it already includes the member discount. */
  discountPence: number;
  /** Items after every item discount, the member one included. */
  paidWithoutDiscountPence: number;
  /** Member discount of the visible "Member discount" line; `0` when it is hidden. */
  memberDiscountPence: number;
}

/** Breakdown lines that show the member discount once, in pence. */
export interface CartBreakdownLines {
  /** "Discount" line: every discount except the member one. */
  discountPence: number;
  /** "Member discount" line. */
  memberDiscountPence: number;
  /** Checkout "Subtotal": items before the member discount. */
  subtotalPence: number;
}

/**
 * Cart total of a company without converted prices, in pence.
 *
 * `membership_discount_total` (per order) is not subtracted: the backend puts
 * the member discount into every item's `discount_amount` and `paid_price`, so
 * `discount_total` and `paid_total` already include it (JS-14484).
 */
export const cartPaidWithDiscountPence = ({
  base_total,
  discount_total,
  promocode_discount,
  paid_bonuses,
  paid_loyalty_points,
}: CartPaidTotalsInput): number =>
  base_total -
  (discount_total + promocode_discount + paid_bonuses + paid_loyalty_points);

/**
 * Free-delivery threshold of the cart, in pence. The calculated orders are the
 * single source — the backend puts a member's threshold there — taken from the
 * first order that states one; the ETA threshold is the fallback until the
 * first calculate answers.
 */
export const freeDeliveryThresholdPence = (
  orders: readonly FreeDeliveryThresholdOrder[],
  etaThresholdPence: number,
): number => {
  const threshold = orders
    .map((order) => order.delivery_info?.threshold)
    .find(
      (value): value is number =>
        typeof value === 'number' && Number.isFinite(value) && value >= 0,
    );
  return threshold ?? etaThresholdPence;
};
